import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  tenantsApi,
  ApiError,
  type TenantFull,
  type ProvisionStepResult,
  type FirstClinicAdminResult,
} from '../api/client';

type ProvisionPhase =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'done'; steps: ProvisionStepResult[] }
  | { kind: 'error'; message: string };

type AdminPhase =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'created'; result: FirstClinicAdminResult }
  | { kind: 'error'; message: string };

export function TenantProvisioningPage() {
  const { id } = useParams();
  const [tenant, setTenant] = useState<TenantFull | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [provision, setProvision] = useState<ProvisionPhase>({ kind: 'idle' });

  // First-Clinic-Admin form state
  const [adminEmail, setAdminEmail] = useState('');
  const [adminFirst, setAdminFirst] = useState('');
  const [adminLast, setAdminLast] = useState('');
  const [adminPhone, setAdminPhone] = useState('');
  const [admin, setAdmin] = useState<AdminPhase>({ kind: 'idle' });

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setTenant(null);
    setLoadError(null);
    (async () => {
      try {
        const r = await tenantsApi.get(id);
        if (!cancelled) setTenant(r.tenant);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.body.message ?? 'Failed to load' : 'Failed to load');
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (!id) return null;

  if (loadError) {
    return (
      <div className="space-y-3">
        <Link to={`/tenants/${id}`} className="text-xs text-ink-muted hover:text-ink uppercase tracking-wider">
          ← Tenant
        </Link>
        <p className="text-bad">{loadError}</p>
      </div>
    );
  }

  if (!tenant) return <p className="text-sm text-ink-muted">Loading…</p>;

  const brandReady = !!tenant.brand_primary_color && !!tenant.brand_default_color_mode;
  // The TenantFull type doesn't expose offerings_extraction_accepted_at directly,
  // so we fall back to lifecycle: provisioning checks the gate server-side anyway.
  const isPending = tenant.lifecycle_state === 'PENDING';
  const isActive = tenant.lifecycle_state === 'ACTIVE';
  const hasRealD1 = !tenant.d1_database_id.startsWith('pending-');

  async function runProvision() {
    setProvision({ kind: 'running' });
    try {
      const r = await tenantsApi.provision(tenant!.id);
      setTenant(r.tenant);
      setProvision({ kind: 'done', steps: r.steps });
    } catch (err) {
      setProvision({
        kind: 'error',
        message: err instanceof ApiError
          ? err.body.message ?? err.body.error ?? 'Provisioning failed'
          : 'Provisioning failed',
      });
    }
  }

  async function submitAdmin(ev: React.FormEvent) {
    ev.preventDefault();
    if (!adminEmail.trim() || !adminFirst.trim() || !adminLast.trim()) return;
    setAdmin({ kind: 'submitting' });
    try {
      const r = await tenantsApi.createFirstClinicAdmin(tenant!.id, {
        email: adminEmail.trim(),
        first_name: adminFirst.trim(),
        last_name: adminLast.trim(),
        phone: adminPhone.trim() ? adminPhone.trim() : null,
      });
      setAdmin({ kind: 'created', result: r });
    } catch (err) {
      setAdmin({
        kind: 'error',
        message: err instanceof ApiError
          ? err.body.message ?? err.body.error ?? 'Failed to create admin'
          : 'Failed to create admin',
      });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to={`/tenants/${id}`} className="text-xs text-ink-muted hover:text-ink uppercase tracking-wider">
          ← {tenant.display_name}
        </Link>
        <h1 className="font-heading text-2xl font-semibold tracking-tight mt-2">
          Provision &amp; Onboard
        </h1>
        <p className="text-sm text-ink-muted mt-1">
          Create the tenant database, copy master reference data, seed clinic offerings, and invite the first Clinic Admin.
        </p>
      </div>

      {/* ── Pre-flight checks ─────────────────────────────────────────── */}
      <section className="rounded-2xl bg-surface-2 p-6 space-y-3">
        <h2 className="font-heading text-lg">Pre-flight</h2>
        <ul className="text-sm space-y-1.5">
          <Check label="Tenant record created" ok={true} />
          <Check label="Brand kit accepted" ok={brandReady} hint={!brandReady ? 'Run brand extraction first.' : undefined} />
          <Check
            label="Clinic offerings accepted"
            // Server-side gate is authoritative; we only show the button if PENDING.
            ok={!isPending || hasRealD1 || isActive}
            hint={isPending && !hasRealD1 ? 'Run offerings extraction first if not done.' : undefined}
          />
          <Check label="D1 database created" ok={hasRealD1} />
          <Check label="Tenant ACTIVE" ok={isActive} />
        </ul>
      </section>

      {/* ── Provisioning step ────────────────────────────────────────── */}
      {!isActive && (
        <section className="rounded-2xl bg-surface-2 p-6 space-y-3">
          <h2 className="font-heading text-lg">Step 1 — Run provisioning</h2>
          <p className="text-sm text-ink-muted">
            This creates the per-tenant D1 database, applies the tenant migration, copies master biomarkers /
            food items / oligoscan elements from the control plane, seeds the accepted clinic offerings, and
            transitions the tenant to ACTIVE. Safe to retry.
          </p>

          {provision.kind === 'idle' && (
            <button
              onClick={runProvision}
              disabled={!isPending || !brandReady}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-bg disabled:opacity-50 hover:bg-primary/90"
            >
              Provision now
            </button>
          )}
          {provision.kind === 'running' && (
            <p className="text-sm text-ink-muted">Provisioning… (may take 10–30 seconds)</p>
          )}
          {provision.kind === 'done' && <StepList steps={provision.steps} />}
          {provision.kind === 'error' && (
            <div className="space-y-2">
              <p className="text-sm text-bad">{provision.message}</p>
              <button
                onClick={runProvision}
                className="rounded-lg bg-primary/15 px-3 py-1.5 text-sm text-primary hover:bg-primary/25"
              >
                Retry
              </button>
            </div>
          )}
        </section>
      )}

      {/* ── First Clinic Admin ───────────────────────────────────────── */}
      {isActive && (
        <section className="rounded-2xl bg-surface-2 p-6 space-y-4">
          <h2 className="font-heading text-lg">Step 2 — Invite first Clinic Admin</h2>
          <p className="text-sm text-ink-muted">
            Creates a CLINIC_ADMIN user inside the tenant database and emails them a temporary password.
            The admin is required to change their password and complete email 2FA on first login.
          </p>

          {admin.kind !== 'created' && (
            <form onSubmit={submitAdmin} className="space-y-3 max-w-xl">
              <Field label="Email" required>
                <input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  required
                  className="w-full rounded-md bg-surface-3 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" required>
                  <input
                    value={adminFirst}
                    onChange={(e) => setAdminFirst(e.target.value)}
                    required
                    className="w-full rounded-md bg-surface-3 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </Field>
                <Field label="Last name" required>
                  <input
                    value={adminLast}
                    onChange={(e) => setAdminLast(e.target.value)}
                    required
                    className="w-full rounded-md bg-surface-3 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </Field>
              </div>
              <Field label="Phone (optional)">
                <input
                  value={adminPhone}
                  onChange={(e) => setAdminPhone(e.target.value)}
                  className="w-full rounded-md bg-surface-3 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </Field>

              {admin.kind === 'error' && (
                <p role="alert" className="text-sm text-bad">{admin.message}</p>
              )}

              <button
                type="submit"
                disabled={admin.kind === 'submitting'}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-bg disabled:opacity-50 hover:bg-primary/90"
              >
                {admin.kind === 'submitting' ? 'Creating…' : 'Create Clinic Admin'}
              </button>
            </form>
          )}

          {admin.kind === 'created' && (
            <div className="space-y-3 rounded-xl bg-good/10 p-4 border border-good/30">
              <p className="text-sm text-good font-medium">
                ✓ Clinic Admin created
                {admin.result.emailSent
                  ? ' and welcome email sent.'
                  : ' (welcome email FAILED — copy the password below).'}
              </p>
              <DefRow label="Email" value={<code className="text-xs">{admin.result.email}</code>} />
              <DefRow
                label="Temporary password"
                value={
                  <code className="text-xs bg-surface-3 px-2 py-1 rounded select-all">
                    {admin.result.temporaryPassword}
                  </code>
                }
              />
              <DefRow
                label="Login URL"
                value={
                  <a href={admin.result.loginUrl} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline break-all">
                    {admin.result.loginUrl}
                  </a>
                }
              />
              {!admin.result.emailSent && admin.result.emailError && (
                <p className="text-xs text-bad">Email error: {admin.result.emailError}</p>
              )}
              <p className="text-xs text-ink-muted border-t border-surface-3 pt-3">
                The temporary password is shown once. The admin will be required to change it on first login.
              </p>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function Check({ label, ok, hint }: { label: string; ok: boolean; hint?: string }) {
  return (
    <li className="flex items-start gap-2">
      <span className={ok ? 'text-good' : 'text-ink-muted'}>{ok ? '✓' : '○'}</span>
      <div>
        <span className={ok ? 'text-ink' : 'text-ink-muted'}>{label}</span>
        {hint && <p className="text-xs text-ink-muted">{hint}</p>}
      </div>
    </li>
  );
}

function StepList({ steps }: { steps: ProvisionStepResult[] }) {
  return (
    <ul className="space-y-2 text-sm">
      {steps.map((s, i) => (
        <li key={i} className="flex items-start gap-3 rounded-md bg-surface-3 px-3 py-2">
          <span className={s.status === 'completed' ? 'text-good' : 'text-ink-muted'}>
            {s.status === 'completed' ? '✓' : '○'}
          </span>
          <div className="flex-1">
            <p className="text-ink">{prettyStepName(s.step)} <span className="text-xs text-ink-muted">({s.status})</span></p>
            {s.details && (
              <pre className="mt-1 text-xs text-ink-muted whitespace-pre-wrap break-all">
                {JSON.stringify(s.details, null, 2)}
              </pre>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function prettyStepName(step: ProvisionStepResult['step']): string {
  switch (step) {
    case 'D1_CREATE': return 'Create D1 database';
    case 'MIGRATION': return 'Run tenant migration';
    case 'MASTER_DATA_COPY': return 'Copy master reference data';
    case 'OFFERINGS_SEED': return 'Seed clinic offerings';
    case 'CUSTOM_DOMAIN': return 'Attach platform subdomain';
    case 'ACTIVATE': return 'Mark tenant ACTIVE';
  }
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block text-sm space-y-1">
      <span className="text-xs uppercase tracking-wider text-ink-muted">
        {label}{required && <span className="text-bad ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function DefRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <span className="text-xs uppercase tracking-wider text-ink-muted w-32 shrink-0">{label}</span>
      <span className="text-ink break-all">{value}</span>
    </div>
  );
}
